import React, { useState } from 'react';
import { X, Globe } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import AccountPicker from './AccountPicker';

// Add, link, or edit a domain.
//
//   - Add mode (no `domain` prop): pick an account, enter the hostname, and
//     optionally link a Cloudflare zone id. POSTs /api/admin/domains.
//   - Edit mode (`domain` prop set): change the manual fields — registrar,
//     renewal date, SSL status, cutover date, notes. PATCHes
//     /api/admin/domains/:id. Account + zone linkage aren't editable here (the
//     PATCH endpoint doesn't move accounts, and the CF sync owns the zone
//     fields), so they're hidden in edit mode.
//
// On success the parent reloads the list.

const inputCls = 'w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white text-sm focus:outline-none focus:border-[#B0FC38]/40';
const labelCls = 'block text-[11px] uppercase tracking-wider text-gray-400/80 mb-1.5';
const dateOnly = (v) => (v ? String(v).slice(0, 10) : '');

export default function DomainModal({ domain = null, onClose, onSaved }) {
  const isEdit = !!domain;
  const cfManaged = isEdit && !!domain.cfZoneId;

  const [accountId, setAccountId] = useState(domain?.accountId || '');
  const [host, setHost] = useState(domain?.domain || '');
  const [cfZoneId, setCfZoneId] = useState(domain?.cfZoneId || '');
  const [registrar, setRegistrar] = useState(domain?.registrar || '');
  const [registrarRenewalAt, setRegistrarRenewalAt] = useState(dateOnly(domain?.registrarRenewalAt));
  const [sslStatus, setSslStatus] = useState(domain?.sslStatus || '');
  const [cutoverCompletedAt, setCutoverCompletedAt] = useState(dateOnly(domain?.cutoverCompletedAt));
  const [notes, setNotes] = useState(domain?.notes || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const canSubmit = (isEdit ? host.trim() : (accountId && host.trim())) && !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      if (isEdit) {
        await api.patch(`/api/admin/domains/${domain.id}`, {
          domain: host.trim(),
          registrar: registrar.trim() || null,
          registrarRenewalAt: registrarRenewalAt || null,
          sslStatus: sslStatus.trim() || null,
          cutoverCompletedAt: cutoverCompletedAt || null,
          notes: notes.trim() || null,
        });
      } else {
        await api.post('/api/admin/domains', {
          accountId,
          domain: host.trim(),
          cfZoneId: cfZoneId.trim() || null,
          registrar: registrar.trim() || null,
          registrarRenewalAt: registrarRenewalAt || null,
          notes: notes.trim() || null,
        });
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : `Failed to ${isEdit ? 'save' : 'add'} domain`);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-[#0a0a0a] border border-white/[0.08] rounded-2xl shadow-2xl">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-2 min-w-0">
            <Globe className="w-4 h-4 text-[#B0FC38] shrink-0" />
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-white tracking-tight">{isEdit ? 'Edit domain' : 'Add / link domain'}</h2>
              <p className="text-[12px] text-gray-500 mt-0.5 truncate">
                {isEdit ? domain.domain : 'Register a domain for a school, or link its Cloudflare zone.'}
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-white shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="px-5 py-4 space-y-4">
          {error && (
            <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-[12px] text-red-300">
              {error}
            </div>
          )}

          {!isEdit && (
            <div>
              <label className={labelCls}>Account</label>
              <AccountPicker value={accountId} onChange={setAccountId} required ariaInvalid={!accountId && !!error} />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Domain</label>
              <input value={host} onChange={(e) => setHost(e.target.value)} placeholder="example.org" className={inputCls} />
            </div>
            {!isEdit ? (
              <div>
                <label className={labelCls}>Cloudflare zone ID <span className="text-gray-600 normal-case">(optional)</span></label>
                <input value={cfZoneId} onChange={(e) => setCfZoneId(e.target.value)} placeholder="link an existing zone" className={inputCls} />
              </div>
            ) : (
              <div>
                <label className={labelCls}>SSL status <span className="text-gray-600 normal-case">(optional)</span></label>
                <input value={sslStatus} onChange={(e) => setSslStatus(e.target.value)} placeholder="active, pending…" className={inputCls} />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Registrar <span className="text-gray-600 normal-case">(optional)</span></label>
              <input value={registrar} onChange={(e) => setRegistrar(e.target.value)} placeholder="GoDaddy, Namecheap…" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Renewal date <span className="text-gray-600 normal-case">(optional)</span></label>
              <input type="date" value={registrarRenewalAt} onChange={(e) => setRegistrarRenewalAt(e.target.value)} className={inputCls} />
            </div>
          </div>

          {isEdit && (
            <div>
              <label className={labelCls}>Cutover completed <span className="text-gray-600 normal-case">(optional)</span></label>
              <input type="date" value={cutoverCompletedAt} onChange={(e) => setCutoverCompletedAt(e.target.value)} className={inputCls} />
            </div>
          )}

          <div>
            <label className={labelCls}>Notes <span className="text-gray-600 normal-case">(optional)</span></label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} />
          </div>

          {cfManaged && (
            <p className="text-[11px] text-gray-500">
              Domain &amp; status are kept in sync from Cloudflare. Registrar, renewal date, and notes are yours to set here.
            </p>
          )}

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/[0.05]">
            <button type="button" onClick={onClose} className="px-4 py-2 text-[12px] text-gray-400 hover:text-white">Cancel</button>
            <button type="submit" disabled={!canSubmit}
              className="px-4 py-2 rounded-full bg-[#B0FC38] text-gray-900 text-[13px] font-medium hover:bg-[#9BE22E] disabled:opacity-50">
              {submitting ? 'Saving…' : (isEdit ? 'Save changes' : 'Add domain')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
